import React, {useMemo} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

import {Box, Typography} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import CalendarEmbed from 'components/CalendarEmbed'
import {buildProgramSessionMetadata} from 'utils/calendarMetadata'
import {getBookingFieldsForProgram} from 'utils/programBookingFields'
import {shouldLockAttendeeFields} from 'utils/programLockAttendeeFields'
import {buildRoutingFormResponses} from 'utils/routingFormResponses'

import {ReactComponent as UsersIcon} from '@progyny/assets/icons/users.svg'
import {
  ConsultationsProgramDetailQuery,
  useConsultationsMyAppointmentsQuery,
} from '@progyny/generated/graphql'
import {hasRichTextContent} from '@progyny/pages/Consultations/components/AppointmentCard'
import {ConsultationCard} from '@progyny/pages/Consultations/components/ConsultationCard'

export interface FertilityConsultationItem {
  sessionIndex: string
  title: string
  subtitle: string
  description: string
  appointment: {
    id: string
    calcomBookingUid?: string | null
    canReschedule?: boolean | null
    canJoin?: boolean | null
    meetingLink?: string | null
    scheduledTime?: string | null
    status: string
  }
  required: boolean
}

type Program = ConsultationsProgramDetailQuery['consultations']['program']
type EnrollmentArray =
  ConsultationsProgramDetailQuery['consultations']['myProgramEnrollments']
type Enrollment = NonNullable<EnrollmentArray>[number]

export interface FertilitySessionProps {
  colors?: any
  classes: {
    consultationContainer: string
    contentSection: string
    header: string
    iconFrame: string
    icon: string
    title: string
    content: string
    buttonContainer: string
    outlinedButton: string
  }
  program: Program
  programEnrollment: Enrollment | null | undefined
  programSlug: string | undefined
  user:
    | {id?: string; email?: string; firstName?: string; lastName?: string}
    | null
    | undefined
  countryCode: string | undefined
  t: (key: string) => string
}

const FertilitySession: React.FC<FertilitySessionProps> = ({
  colors,
  classes,
  program,
  programEnrollment,
  programSlug,
  user,
  countryCode,
  t,
}) => {
  const history = useHistory()
  const location = useLocation()
  const {data: appointmentsData} = useConsultationsMyAppointmentsQuery({
    fetchPolicy: 'cache-first',
  })

  const fertilityConsultations = useMemo(() => {
    const allAppointments =
      appointmentsData?.consultations?.myAppointments || []

    const scheduledAppointments = allAppointments.filter(
      (appointment): appointment is NonNullable<typeof appointment> =>
        appointment !== null &&
        appointment !== undefined &&
        appointment.status === 'SCHEDULED' &&
        appointment.programEnrollment?.program?.slug === programSlug
    )

    return scheduledAppointments
      .map((appointment) => {
        const sessionStatus = programEnrollment?.sessionStatuses?.find(
          (ss) => ss?.appointment?.id === appointment?.id
        )
        const sessionItem = sessionStatus?.session
        return {
          sessionIndex: String(sessionItem?.sessionNumber || ''),
          title: sessionItem?.title || '',
          subtitle: sessionItem?.subtitle || '',
          description: sessionItem?.description || '',
          appointment: {
            id: appointment.id,
            calcomBookingUid: appointment.calcomBookingUid,
            canReschedule: appointment.canReschedule,
            canJoin: appointment.canJoin,
            meetingLink: appointment.meetingLink,
            scheduledTime: appointment.scheduledTime,
            status: appointment.status,
          },
          required: sessionItem?.required ?? true,
        }
      })
      .sort((a: FertilityConsultationItem, b: FertilityConsultationItem) => {
        const timeA = a.appointment?.scheduledTime
          ? new Date(a.appointment.scheduledTime).getTime()
          : Infinity
        const timeB = b.appointment?.scheduledTime
          ? new Date(b.appointment.scheduledTime).getTime()
          : Infinity
        return timeA - timeB
      })
  }, [appointmentsData, programEnrollment, programSlug])

  const session = program?.sessions?.find((s) => s?.sessionNumber === 1)

  const handleLearnMoreClick = (sessionIndex: string) => {
    history.push(`/consultations/program/${programSlug}/info/${sessionIndex}`, {
      referrer: location.pathname,
    })
  }

  const handleRescheduleClick = (appointment: any, sessionIndex: string) => {
    if (appointment?.calcomBookingUid) {
      history.push(
        `/consultations/program/${programSlug}/book?session=${sessionIndex}&rescheduleUid=${appointment.calcomBookingUid}`
      )
    }
  }

  const handleJoinMeetingClick = (appointment: any) => {
    if (appointment?.meetingLink) {
      window.open(appointment.meetingLink, '_blank')
    }
  }

  if (Boolean(fertilityConsultations.length)) {
    return (
      <Box>
        <ConsultationCard
          padding='md'
          borderRadius='md'
          className={classes.consultationContainer}
        >
          {fertilityConsultations.map(
            (sessionItem: FertilityConsultationItem, index: number) => {
              return (
                <Box key={index} sx={{containerType: 'inline-size'}}>
                  <Box className={classes.contentSection}>
                    <Box className={classes.header}>
                      <Box>
                        <Box
                          className={classes.iconFrame}
                          sx={{color: colors?.primary || '#148199'}}
                        >
                          <UsersIcon
                            className={classes.icon}
                            style={{
                              color: 'inherit',
                              fontSize: '24px',
                            }}
                          />
                        </Box>
                        <Typography className={classes.title} variant='h4'>
                          {t(
                            'consultations_program_fertility_session_1_1_consultation_with_progyny'
                          )}
                        </Typography>
                      </Box>

                      <Box className={classes.content}>
                        {t(
                          'consultations_program_fertility_session_we_re_here_to_help'
                        )}
                      </Box>
                    </Box>

                    <Box className={classes.buttonContainer}>
                      {hasRichTextContent(sessionItem?.description) && (
                        <PGButton
                          variant='text'
                          label={t('consultations_program_session_see_details')}
                          onClick={() =>
                            handleLearnMoreClick(sessionItem?.sessionIndex)
                          }
                          size='medium'
                        />
                      )}
                      <PGButton
                        variant='text'
                        label={t(
                          'consultations_program_fertility_session_reschedule'
                        )}
                        onClick={() =>
                          handleRescheduleClick(
                            sessionItem?.appointment,
                            sessionItem?.sessionIndex
                          )
                        }
                        disabled={
                          !sessionItem?.appointment?.canReschedule ||
                          !sessionItem?.appointment?.calcomBookingUid
                        }
                        size='medium'
                      />
                      <PGButton
                        variant='outlined'
                        label={t(
                          'consultations_program_fertility_session_join_meeting'
                        )}
                        className={classes.outlinedButton}
                        onClick={() =>
                          handleJoinMeetingClick(sessionItem?.appointment)
                        }
                        disabled={
                          !sessionItem?.appointment?.canJoin ||
                          !sessionItem?.appointment?.meetingLink
                        }
                        size='medium'
                      />
                    </Box>
                  </Box>
                </Box>
              )
            }
          )}
        </ConsultationCard>
      </Box>
    )
  }

  if (session?.calcomEventType) {
    return (
      <Box>
        <CalendarEmbed
          calLink={session.calcomEventType}
          title={session.title ?? undefined}
          description={session.description ?? undefined}
          additionalContent={session.bookingDescription ?? undefined}
          prefillData={{
            email: user?.email || '',
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          }}
          metadata={buildProgramSessionMetadata(
            user?.id || '',
            session?.id || ''
          )}
          bookingFields={
            programSlug ? getBookingFieldsForProgram(programSlug) : undefined
          }
          lockAttendeeFields={shouldLockAttendeeFields(programSlug)}
          routingFormResponses={buildRoutingFormResponses(countryCode)}
        />
      </Box>
    )
  }

  return null
}

export default FertilitySession
